import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ChartContainer } from "../ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
} from "recharts";
import {
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Percent,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useZonePagination } from "../../../hooks/basic-reports/use-meter-pagination";
import MeterPagination from "./MeterPagination";
import ExportButton from "./ExportButton";
import { CHIP_COLORS } from "../utils/colors";
import { useReportData } from "../../../contexts/ReportDataContext";

const chartConfig = {
  consumption: { label: "Consumption (kWh)", color: CHIP_COLORS.info },
};

const ITEMS_PER_PAGE = 10;

const EnergyReports = () => {
  const {
    reports,
    loading,
    error,
    meterCount: totalMeters,
    progress,
  } = useReportData();
  const [showMeterTable, setShowMeterTable] = useState(true);
  const { getPage, setPage } = useZonePagination();

  if (loading)
    return (
      <div className="p-10 text-center">
        <div className="animate-pulse text-muted-foreground">
          {progress || "Loading Report Data..."}
        </div>
      </div>
    );
  if (error)
    return <div className="p-10 text-center text-destructive">{error}</div>;
  if (!reports)
    return <div className="p-10 text-center">No data available</div>;

  const { energy } = reports;

  // Hourly consumption chart data
  const hourlyConsumption = energy.hourlyChartData.map((d) => ({
    hour: d.time,
    consumption: d.kwh,
    label: d.time,
  }));

  // Per-meter energy distribution for pie chart
  const energyDistribution = energy.distribution.map((d: any) => ({
    name: d.name,
    value: d.value,
    kwh: d.kwh,
    color: d.color,
  }));

  // Per-meter data for the table
  const perMeterData = energy.perMeter;

  // Compute efficiency (kWh / kVAh = power factor proxy)
  const avgEfficiency =
    energy.totalKVAh > 0 ? energy.totalKWh / energy.totalKVAh : 0;

  // Peak hour: find the hour with highest consumption
  const peakHourEntry = hourlyConsumption.reduce(
    (max, d) => (d.consumption > max.consumption ? d : max),
    hourlyConsumption[0] || { hour: "N/A", consumption: 0, label: "N/A" },
  );

  // Pagination for meter table
  const currentPage = getPage("meters");
  const totalPages = Math.ceil(perMeterData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, perMeterData.length);
  const paginatedMeters = perMeterData.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-6 gap-4">
        <Card className="col-span-1">
          <CardContent className="pt-7">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ArrowDownRight className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Import</p>
                <p className="text-lg font-bold">
                  {energy.totalKWh.toLocaleString()} kWh
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-7">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <ArrowUpRight className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Export</p>
                <p className="text-lg font-bold">
                  {energy.totalKWhExp.toLocaleString()} kWh
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-7">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent">
                <Zap className="w-4 h-4 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Net Energy</p>
                <p className="text-lg font-bold">
                  {(energy.totalKWh - energy.totalKWhExp).toLocaleString()} kWh
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-7">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary">
                <Activity className="w-4 h-4 text-secondary-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Apparent Energy</p>
                <p className="text-lg font-bold">
                  {energy.totalKVAh.toLocaleString()} kVAh
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-7">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Percent className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg Efficiency</p>
                <p className="text-lg font-bold">
                  {(avgEfficiency * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-7">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <Zap className="w-4 h-4 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Peak Hour ({peakHourEntry.consumption.toLocaleString()} kWh)
                </p>
                <p className="text-lg font-bold">{peakHourEntry.hour}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-8">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">
                  Hourly Energy Consumption Pattern
                </CardTitle>
                <CardDescription>
                  Aggregate consumption across {totalMeters} meters
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                Hourly Aggregation
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <BarChart
                data={hourlyConsumption}
                margin={{ top: 10, right: 10, left: 40, bottom: 30 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.5}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: "Time of Day",
                    position: "insideBottom",
                    offset: -5,
                    fontSize: 11,
                    fill: "hsl(var(--muted-foreground))",
                  }}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: "Energy (kWh)",
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 11,
                    fill: "hsl(var(--muted-foreground))",
                  }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-background border rounded-lg shadow-lg p-3">
                          <p className="font-medium">
                            {payload[0].payload.label}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {Number(payload[0].value).toLocaleString()} kWh
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="consumption"
                  fill={CHIP_COLORS.info}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="col-span-4">
          <CardHeader className="pb-2">
            <CardDescription>
              Consumption share across {totalMeters} meters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={energyDistribution}
                    cx="50%"
                    cy="45%"
                    innerRadius={65}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {energyDistribution.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium">{data.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {data.value}% of total
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {data.kwh?.toLocaleString()} kWh
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* <Legend
                    verticalAlign="bottom"
                    height={80}
                    formatter={(value: any) => (
                      <span className="text-xs text-muted-foreground ">
                        {value}
                      </span>
                    )}
                  /> */}
                </PieChart>
              </ResponsiveContainer>
              <p className="text-md  text-center">
                {perMeterData.length + 1} Meters Consumption share
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-Meter Energy Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">
                Per-Meter Energy Summary
              </CardTitle>
              <CardDescription>
                Detailed energy data for {perMeterData.length} meters
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton reportName="Meter Energy Summary" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-primary"
                onClick={() => setShowMeterTable(!showMeterTable)}
              >
                {showMeterTable ? "Hide Details" : "Show Details"}
                {showMeterTable ? (
                  <ChevronDown className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronRight className="w-4 h-4 ml-1" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showMeterTable && (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter ID</TableHead>
                  <TableHead className="text-right">Import (kWh)</TableHead>
                  <TableHead className="text-right">Export (kWh)</TableHead>
                  <TableHead className="text-right">Apparent (kVAh)</TableHead>
                  <TableHead className="text-right">Peak kW</TableHead>
                  <TableHead className="text-right">Avg kW</TableHead>
                  {/* <TableHead className="text-right">Readings</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMeters.map((meter) => (
                  <TableRow key={meter.meterId} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      {meter.meterId.replace(/^Demo1_/, "")}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.totalKWh.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.totalKWhExp.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.totalKVAh.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.peakKW.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.avgKW.toLocaleString()}
                    </TableCell>
                    {/* <TableCell className="text-right">
                      <Badge variant="secondary" className="font-mono">
                        {meter.readingCount}
                      </Badge>
                    </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <MeterPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={perMeterData.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={(page) => setPage("meters", page)}
              hasNextPage={currentPage < totalPages}
              hasPrevPage={currentPage > 1}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default EnergyReports;
